import React, { useState, useEffect, useMemo } from "react";
import { Box, Stack } from "@mui/system";
import {
  Typography, Paper, Grid, Avatar, Skeleton, Fade, Chip
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import { Line, Bar } from "react-chartjs-2";
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  BarElement,
  Title, 
  Tooltip, 
  Filler 
} from 'chart.js';

import Sidebar from "./Sidebar";

// Icons (Assuming paths are correct)
import progressFunnel from "../../Images/funnel-icon.svg";
import openLeadIcon from "../../Images/open-lead-icon.svg";
import dueFollowupIcon from "../../Images/miss-call-icon.svg";
import customerIcon from "../../Images/customers.svg";
import "../../fonts/fonts.css";

// MUI Icons
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

// --- OPTIMIZATION 1: Manual Register (Tree Shaking) ---
ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Filler
);

// --- STYLED COMPONENTS ---
const RootContainer = styled(Box)({
  background: "#F8FAFC", 
  minHeight: "100vh",
  display: "flex",
  width: "100%",
});

const ContentArea = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  marginLeft: "18vw",
  padding: "40px",
  width: "calc(100% - 18vw)",
  maxWidth: "1600px",
  boxSizing: "border-box",
  transition: "all 0.3s ease",
  [theme.breakpoints.down("md")]: {
    marginLeft: 0,
    width: "100%",
    padding: "20px",
    paddingTop: "84px", // Extra padding for the fixed mobile AppBar
  },
}));

const MetricCard = styled(Paper)(({ accentcolor }) => ({
  padding: "24px",
  borderRadius: "16px",
  background: "#FFFFFF",
  border: "1px solid #E2E8F0",
  display: "flex",
  alignItems: "center",
  gap: "16px",
  boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
  transition: "transform 0.15s ease-out, box-shadow 0.15s ease-out, border-color 0.15s ease-out",
  willChange: "transform",
  height: "110px", 
  width: "100%",
  boxSizing: "border-box",
  "&:hover": {
    borderColor: accentcolor,
    transform: "translateY(-2px)",
    boxShadow: `0 12px 20px -5px ${accentcolor}15`,
  }
}));

const ChartContainer = styled(Paper)({
  background: "#FFFFFF",
  borderRadius: "20px",
  padding: "28px",
  border: "1px solid #E2E8F0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  width: "100%",
  marginBottom: "24px",
  height: "380px", 
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
});

const IconWrapper = styled(Box)(({ color }) => ({
  width: "56px",
  height: "56px",
  borderRadius: "14px",
  backgroundColor: color,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  boxShadow: `0 4px 10px ${color}40`,
  "& img": {
    width: "28px",
    height: "28px",
    filter: "brightness(0) invert(1)"
  }
}));

const StatBadge = styled(Box)({
  backgroundColor: '#F8FAFC',
  padding: '10px 18px',
  borderRadius: '12px',
  border: '1px solid #E2E8F0',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  minWidth: '100px',
  height: '64px', 
  justifyContent: 'center',
  boxSizing: 'border-box',
});

// --- HELPER HOOK FOR DATA FETCHING (PROGRESSIVE) ---
const useDashboardData = () => {
  // Split state for progressive loading - stats appear first!
  const [stats, setStats] = useState({ open: 0, progress: 0, due: 0, clients: 0 });
  const [statsLoaded, setStatsLoaded] = useState(false);
  const [chartData, setChartData] = useState({
    leads: [], calls: [], visits: [],
    leadsLabels: [], callsLabels: [], visitsLabels: [],
    leadsAvg: 0, callsAvg: 0, visitsAvg: 0,
    pendingProspects: [], pendingProspectsTotal: 0,
  });
  const [chartsLoaded, setChartsLoaded] = useState(false);
  const [fact, setFact] = useState(null);

  useEffect(() => {
    const roleID = localStorage.getItem('roleId');
    const token = localStorage.getItem("accessToken");
    const fullName = localStorage.getItem('fullName');
    const config = { headers: { auth: token } };
    const isAdmin = roleID === "af7c1fe6-d669-414e-b066-e9733f0de7a8";
    const getUrl = (adminEndpoint, userEndpoint) => 
      isAdmin ? adminEndpoint : `${userEndpoint}/${fullName}`;
    const getAvg = (arr) => arr.length ? parseFloat((arr.reduce((a, b) => a + b, 0) / arr.length).toFixed(1)) : 0;

    // PHASE 1: Load stats first (fastest endpoints)
    const loadStats = async () => {
      try {
        const [open, prog, due, client] = await Promise.all([
          axios.get(getUrl('/openLeads/admin', '/openLeads'), config),
          axios.get(getUrl('/progressTotalLeads/admin', '/progressTotalLeads'), config),
          axios.post(getUrl('/totalDueFollowUps/admin', '/totalDueFollowUps'), [], config),
          axios.post(getUrl('/totalCustomersCount/admin', '/totalCustomersCount'), [], config),
        ]);
        setStats({ 
          open: open.data.count, 
          progress: prog.data.count, 
          due: due.data.totalDueFollowups, 
          clients: client.data.totalCustomersCount 
        });
        setStatsLoaded(true);
      } catch (e) { console.error("Stats Load Fail", e); }
    };

    // PHASE 2: Load charts (heavier endpoints)
    const loadCharts = async () => {
      try {
        const [leads, calls, visit, factRes, pendingProspectsRes] = await Promise.all([
          axios.get(getUrl('/LeadsCountDayWise', '/LeadsCountDayWiseSales'), config),
          axios.get(getUrl('/CallsCountDayWise', '/CallsCountDayWise'), config),
          axios.get(getUrl('/officeVisit/admin', '/officeVisit'), config),
          axios.get(`/carFacts`, config),
          axios.get(`/api/prospects-pending-by-sales-executive`, config),
        ]);
        const extractData = (res) => res.data.data.map(i => i.DataCount);
        const extractLabels = (res) => res.data.data.map(i => i.DateAdded);
        const leadsData = extractData(leads);
        const callsData = extractData(calls);
        const visitsData = extractData(visit);
        const pendingProspects = Array.isArray(pendingProspectsRes.data.data)
          ? pendingProspectsRes.data.data.map((item) => ({
              salesExecutive: item.salesExecutive,
              pending: Number(item.pending) || 0,
            }))
          : [];

        setChartData({
          leads: leadsData, leadsLabels: extractLabels(leads), leadsAvg: getAvg(leadsData),
          calls: callsData, callsLabels: extractLabels(calls), callsAvg: getAvg(callsData),
          visits: visitsData, visitsLabels: extractLabels(visit), visitsAvg: getAvg(visitsData),
          pendingProspects,
          pendingProspectsTotal: Number(pendingProspectsRes.data.total) || pendingProspects.reduce((sum, item) => sum + item.pending, 0),
        });
        setFact(factRes.data.data[Math.floor(Math.random() * factRes.data.data.length)]);
        setChartsLoaded(true);
      } catch (e) { console.error("Charts Load Fail", e); }
    };

    // Run both phases concurrently - stats will resolve first and show immediately
    loadStats();
    loadCharts();
  }, []);

  return { stats, statsLoaded, ...chartData, chartsLoaded, fact };
};

// --- CHART COMPONENT (Ideally move to separate file for true Lazy Load) ---
const DashboardChart = React.memo(({ chart }) => {
    // Memoize options to prevent re-creation on every render
    const options = useMemo(() => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: { 
        legend: { display: false },
        tooltip: {
          enabled: true,
          backgroundColor: '#1E293B',
          titleFont: { family: 'Poppins', size: 12 },
          bodyFont: { family: 'Poppins', size: 14, weight: 'bold' },
          padding: 12,
          cornerRadius: 8,
          displayColors: false,
        }
      },
      scales: {
        x: { ticks: { color: "#94A3B8", font: { size: 11 } }, grid: { display: false } },
        y: { beginAtZero: true, ticks: { color: "#94A3B8", font: { size: 11 }, maxTicksLimit: 5 }, grid: { color: "#F1F5F9" } }
      }
    }), []);

    const data = useMemo(() => ({
      labels: chart.lbl.map(l => new Date(l).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })),
      datasets: [{
        data: chart.data,
        borderColor: chart.color,
        borderWidth: 3,
        pointRadius: 2,
        pointHoverRadius: 6,
        pointBackgroundColor: chart.color,
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        tension: 0.4,
        fill: true,
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 300);
          gradient.addColorStop(0, `${chart.color}25`);
          gradient.addColorStop(1, `${chart.color}00`);
          return gradient;
        },
      }]
    }), [chart.lbl, chart.data, chart.color]);

    return <Line data={data} options={options} />;
});

const PendingProspectsChart = React.memo(({ data }) => {
    const options = useMemo(() => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          backgroundColor: '#1E293B',
          titleFont: { family: 'Poppins', size: 12 },
          bodyFont: { family: 'Poppins', size: 14, weight: 'bold' },
          padding: 12,
          cornerRadius: 8,
          displayColors: false,
          callbacks: {
            label: (context) => `${context.parsed.y} pending prospects`,
          },
        },
      },
      scales: {
        x: {
          ticks: { color: "#64748B", font: { size: 11, weight: 700 }, maxRotation: 0 },
          grid: { display: false },
        },
        y: {
          beginAtZero: true,
          ticks: { precision: 0, color: "#94A3B8", font: { size: 11 }, maxTicksLimit: 6 },
          grid: { color: "#F1F5F9" },
        },
      },
    }), []);

    const chartData = useMemo(() => ({
      labels: data.map((item) => item.salesExecutive.split(" ")),
      datasets: [{
        data: data.map((item) => item.pending),
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 260);
          gradient.addColorStop(0, "#EF4444");
          gradient.addColorStop(1, "#FEE2E2");
          return gradient;
        },
        borderColor: "#EF4444",
        borderWidth: 1,
        borderRadius: 8,
        maxBarThickness: 48,
      }]
    }), [data]);

    return <Bar data={chartData} options={options} />;
});

// --- MAIN COMPONENT ---
function Feed() {
  const dbData = useDashboardData();
  const userName = localStorage.getItem('fullName');

  // Memoize the metric list to avoid recalculation
  const metrics = useMemo(() => [
    { label: "Progress Pipeline", val: dbData.stats.progress, icon: progressFunnel, color: "#3B82F6" },
    { label: "Open Enquiries", val: dbData.stats.open, icon: openLeadIcon, color: "#10B981" },
    { label: "Pending Tasks", val: dbData.stats.due, icon: dueFollowupIcon, color: "#EF4444" },
    { label: "Global Clients", val: dbData.stats.clients, icon: customerIcon, color: "#6366F1" },
  ], [dbData.stats.progress, dbData.stats.open, dbData.stats.due, dbData.stats.clients]);

  // Memoize charts list
  const charts = useMemo(() => [
    { title: "Leads Generation", avg: dbData.leadsAvg, lbl: dbData.leadsLabels, data: dbData.leads, color: "#3B82F6" },
    { title: "Communication Flow", avg: dbData.callsAvg, lbl: dbData.callsLabels, data: dbData.calls, color: "#10B981" },
    { title: "Office Footfall", avg: dbData.visitsAvg, lbl: dbData.visitsLabels, data: dbData.visits, color: "#6366F1" },
  ], [dbData]);

  const activeExecutives = useMemo(
    () => dbData.pendingProspects.filter((item) => item.pending > 0),
    [dbData.pendingProspects]
  );
  const clearedExecutives = useMemo(
    () => dbData.pendingProspects.filter((item) => item.pending === 0),
    [dbData.pendingProspects]
  );

  return (
    <RootContainer>
      <Sidebar />
      <ContentArea>
        {/* Header */}
        <Stack 
          direction={{ xs: "column", sm: "row" }} 
          justifyContent="space-between" 
          alignItems={{ xs: "flex-start", sm: "center" }} 
          spacing={2}
          sx={{ mb: 5 }}
        >
          <Box>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 800, 
                color: "#1E293B", 
                letterSpacing: "-1.5px", 
                fontFamily: "Kamerik",
                fontSize: { xs: "2rem", md: "3rem" }
              }}
            >
              Performance Hub
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748B", fontWeight: 500, mt: 0.5 }}>
              Welcome back, <strong>{userName}</strong>
            </Typography>
          </Box>
          <Avatar sx={{ width: 48, height: 48, bgcolor: "#eb1d27", fontSize: '1.1rem', fontWeight: 800, boxShadow: "0 4px 12px rgba(235, 29, 39, 0.3)" }}>
            {userName?.charAt(0)}
          </Avatar>
        </Stack>

        {/* Metrics Grid - Staggered reveal */}
        <Grid container spacing={3} sx={{ mb: 5 }}>
          {metrics.map((stat, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <MetricCard accentcolor={stat.color} elevation={0}>
                <IconWrapper color={stat.color}>
                  <img src={stat.icon} alt={stat.label} />
                </IconWrapper>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  {!dbData.statsLoaded ? (
                     <Stack spacing={0.5}>
                       <Skeleton animation="wave" variant="rectangular" width={70} height={36} sx={{ borderRadius: '8px' }} />
                       <Skeleton animation="wave" variant="text" width={110} height={18} />
                     </Stack>
                  ) : (
                    <Fade in={true} timeout={100} style={{ transitionDelay: `${i * 40}ms` }}>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 900, color: "#1E293B", lineHeight: 1.2 }}>
                          {stat.val}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem', display: 'block', mt: 0.5, letterSpacing: '0.5px' }}>
                          {stat.label}
                        </Typography>
                      </Box>
                    </Fade>
                  )}
                </Box>
              </MetricCard>
            </Grid>
          ))}
        </Grid>

        {/* Charts Section */}
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
          <Box sx={{ width: 4, height: 24, bgcolor: "#eb1d27", borderRadius: "2px" }} />
          <Typography variant="h6" sx={{ fontWeight: 800, color: "#1E293B", letterSpacing: '0.5px' }}>
            Activity Trends
          </Typography>
          <TrendingUpIcon sx={{ color: "#64748B", fontSize: 20 }} />
        </Stack>

        <ChartContainer elevation={0} sx={{ height: 'auto', minHeight: 380 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2, flexShrink: 0 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#1E293B", mb: 0.5 }}>
                Pending Prospects by Sales Executive
              </Typography>
              <Typography variant="caption" sx={{ color: "#94A3B8", fontSize: '0.75rem' }}>
                Current pending prospect distribution
              </Typography>
            </Box>
            <StatBadge>
              {!dbData.chartsLoaded ? (
                <Stack spacing={0.5} alignItems="center">
                  <Skeleton animation="wave" variant="text" width={50} height={32} />
                  <Skeleton animation="wave" variant="text" width={70} height={14} />
                </Stack>
              ) : (
                <Fade in={true} timeout={100}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 900, color: "#EF4444", lineHeight: 1 }}>
                      {dbData.pendingProspectsTotal}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px', mt: 0.5 }}>
                      Pending
                    </Typography>
                  </Box>
                </Fade>
              )}
            </StatBadge>
          </Stack>

          {dbData.chartsLoaded && clearedExecutives.length > 0 && (
            <Fade in={true} timeout={150}>
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #ECFDF5 0%, #F0FDF4 100%)',
                  border: '1px solid #BBF7D0',
                  flexShrink: 0,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <CheckCircleRoundedIcon sx={{ color: '#10B981', fontSize: 18 }} />
                  <Typography variant="caption" sx={{ fontWeight: 800, color: '#047857', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    All Clear
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#059669', fontWeight: 600, fontSize: '0.7rem' }}>
                    {clearedExecutives.length} executive{clearedExecutives.length === 1 ? '' : 's'} with no pending
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                  {clearedExecutives.map((item) => (
                    <Chip
                      key={item.salesExecutive}
                      size="small"
                      icon={<CheckCircleRoundedIcon sx={{ fontSize: '14px !important', color: '#10B981 !important' }} />}
                      label={item.salesExecutive}
                      sx={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #BBF7D0',
                        color: '#047857',
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        height: 26,
                        '& .MuiChip-icon': { ml: '6px', mr: '-2px' },
                        '& .MuiChip-label': { px: '8px' },
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            </Fade>
          )}

          <Box sx={{ flexGrow: 1, minHeight: 220, position: 'relative' }}>
            {!dbData.chartsLoaded ? (
              <Skeleton animation="wave" variant="rectangular" width="100%" height="100%" sx={{ borderRadius: '12px' }} />
            ) : activeExecutives.length === 0 ? (
              <Fade in={true} timeout={150}>
                <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ height: '100%' }}>
                  <CheckCircleRoundedIcon sx={{ color: '#10B981', fontSize: 48 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#047857' }}>
                    Everyone is all caught up
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748B' }}>
                    No pending prospects across the team.
                  </Typography>
                </Stack>
              </Fade>
            ) : (
              <Fade in={true} timeout={150}>
                <Box sx={{ height: '100%', width: '100%' }}>
                  <PendingProspectsChart data={activeExecutives} />
                </Box>
              </Fade>
            )}
          </Box>
        </ChartContainer>

        {charts.map((chart, i) => (
          <ChartContainer key={i} elevation={0}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2, flexShrink: 0 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#1E293B", mb: 0.5 }}>
                  {chart.title}
                </Typography>
                <Typography variant="caption" sx={{ color: "#94A3B8", fontSize: '0.75rem' }}>
                  Last 15 days performance metrics
                </Typography>
              </Box>
              <StatBadge>
                {!dbData.chartsLoaded ? (
                  <Stack spacing={0.5} alignItems="center">
                    <Skeleton animation="wave" variant="text" width={50} height={32} />
                    <Skeleton animation="wave" variant="text" width={70} height={14} />
                  </Stack>
                ) : (
                  <Fade in={true} timeout={100} style={{ transitionDelay: `${i * 60}ms` }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" sx={{ fontWeight: 900, color: chart.color, lineHeight: 1 }}>
                        {chart.avg}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px', mt: 0.5 }}>
                        Daily Avg
                      </Typography>
                    </Box>
                  </Fade>
                )}
              </StatBadge>
            </Stack>
            
            <Box sx={{ flexGrow: 1, minHeight: 0, position: 'relative' }}>
              {!dbData.chartsLoaded ? (
                <Skeleton animation="wave" variant="rectangular" width="100%" height="100%" sx={{ borderRadius: '12px' }} />
              ) : (
                <Fade in={true} timeout={150} style={{ transitionDelay: `${i * 80}ms` }}>
                  <Box sx={{ height: '100%', width: '100%' }}>
                     <DashboardChart chart={chart} />
                  </Box>
                </Fade>
              )}
            </Box>
          </ChartContainer>
        ))}

        {/* Insight Card */}
        <Paper elevation={0} sx={{ 
            p: 3.5, borderRadius: "16px", background: "linear-gradient(135deg, #1E293B 0%, #334155 100%)", color: "white",
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' }, 
            gap: 3, position: 'relative', overflow: 'hidden', minHeight: '110px',
            '&::before': { content: '""', position: 'absolute', top: '-50%', right: '-50%', width: '200%', height: '200%', background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)' }
        }}>
          <Box sx={{ backgroundColor: 'rgba(235, 29, 39, 0.2)', p: 2, borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1, width: '56px', height: '56px', flexShrink: 0 }}>
            <LightbulbIcon sx={{ fontSize: 32, color: '#eb1d27' }} />
          </Box>
          <Box sx={{ position: 'relative', zIndex: 1, flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 800, color: "#eb1d27", textTransform: 'uppercase', letterSpacing: 1.5, fontSize: '0.7rem' }}>
                Auto Insight
              </Typography>
              <AssessmentIcon sx={{ fontSize: 14, color: '#eb1d27' }} />
            </Stack>
            {!dbData.chartsLoaded || !dbData.fact ? (
              <Skeleton animation="wave" variant="text" width="85%" height={28} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
            ) : (
              <Fade in={true} timeout={100}>
                <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.6 }}>
                  <strong>{dbData.fact?.title}:</strong>{" "}
                  <span style={{ fontWeight: 400, color: "#CBD5E1" }}>{dbData.fact?.desc}</span>
                </Typography>
              </Fade>
            )}
          </Box>
        </Paper>
      </ContentArea>
    </RootContainer>
  );
}

export default Feed;
